import React, { useState } from "react";
import { AlertTriangle, Loader2, Archive, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

// Checks required notulen fields before a meeting is closed; resolves true = proceed to end, false = keep discussing.
export function useNotulenGate(cid) {
  const [state, setState] = useState(null);
  const gate = () => new Promise((resolve) => {
    api.post(`/conversations/${cid}/notulen-check`).then((r) => {
      if (!r.data.missing?.length) resolve(true); else setState({ ...r.data, resolve });
    }).catch(() => resolve(true));
  });
  const done = (v) => { state?.resolve(v); setState(null); };
  const dialog = state ? (
    <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/60 p-4" data-testid="notulen-check-dialog">
      <div className="w-full max-w-md rounded-2xl bg-white p-5 text-slate-800 shadow-2xl">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-600"><AlertTriangle size={20} /></span>
          <div><p className="text-base font-bold">Notulen belum lengkap</p><p className="mt-0.5 text-sm text-slate-500">Asisten belum menemukan data untuk kolom wajib berikut:</p></div>
        </div>
        <ul className="mt-3 space-y-1.5">
          {state.missing.map((f) => <li key={f} className="rounded-lg bg-amber-50 px-3 py-2 text-sm" data-testid="notulen-missing-field"><span className="font-semibold">{f}</span>{state.notes?.[f] && <span className="text-slate-500"> — {state.notes[f]}</span>}</li>)}
        </ul>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => done(true)} data-testid="notulen-end" className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100">Tetap akhiri</button>
          <button onClick={() => done(false)} data-testid="notulen-continue" className="btn-primary py-2">Lanjutkan pembahasan</button>
        </div>
      </div>
    </div>
  ) : null;
  return { gate, dialog };
}

// Chat header: "Arsipkan" (move the thread to Arsip as-is) and "Buat Ringkasan" (one summary message replaces the thread; confirm first).
export function ChatArchiveActions({ cid, onDone, render }) {
  const [busy, setBusy] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const archive = async () => {
    setBusy("archive");
    try { const r = await api.post(`/conversations/${cid}/archive`); toast.success(`${r.data.archived} pesan dipindah ke Arsip`); await onDone?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengarsipkan"); } finally { setBusy(null); }
  };
  const summarize = async () => {
    setBusy("summary");
    try { const r = await api.post(`/conversations/${cid}/summarize`); setConfirm(false); toast.success(`Ringkasan dibuat · ${r.data.removed} pesan dihapus`); await onDone?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal membuat ringkasan"); } finally { setBusy(null); }
  };
  const btn = "flex h-9 items-center gap-1.5 rounded-lg border border-[#E6EAF2] bg-white px-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 sm:px-3";
  return (
    <>
      {render ? render({ archive, askSummary: () => setConfirm(true), busy }) : <>
      <button onClick={archive} disabled={!!busy} title="Pindahkan percakapan ini ke Arsip apa adanya" data-testid="archive-chat-btn" className={btn}>{busy === "archive" ? <Loader2 size={15} className="animate-spin" /> : <Archive size={15} />} <span className="hidden sm:inline">Arsipkan</span></button>
      <button onClick={() => setConfirm(true)} disabled={!!busy} title="Ringkas seluruh percakapan menjadi satu pesan" data-testid="summarize-chat-btn" className={btn}><ListChecks size={15} /> <span className="hidden sm:inline">Buat Ringkasan</span></button>
      </>}
      {confirm && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/60 p-4" data-testid="summarize-dialog">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 text-slate-800 shadow-2xl">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-600"><AlertTriangle size={20} /></span>
              <div><p className="text-base font-bold">Buat ringkasan percakapan?</p><p className="mt-0.5 text-sm text-slate-500">Seluruh percakapan ini akan diringkas menjadi satu pesan asisten di awal chat, lalu percakapan bisa langsung dilanjutkan.</p></div>
            </div>
            <ul className="mt-3 space-y-1.5 text-sm">
              <li className="rounded-lg bg-amber-50 px-3 py-2 text-amber-800" data-testid="summarize-warning"><span className="font-semibold">Pesan sebelumnya akan dihapus</span> dan tidak bisa dikembalikan.</li>
              <li className="rounded-lg bg-slate-50 px-3 py-2 text-slate-600">File, gambar, dan video hasil tetap tersimpan di Galeri dan Ruang Kerja.</li>
              <li className="rounded-lg bg-slate-50 px-3 py-2 text-slate-600">Ingin menyimpan percakapan utuh? Gunakan <span className="font-semibold">Arsipkan</span>.</li>
            </ul>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setConfirm(false)} disabled={!!busy} data-testid="summarize-cancel" className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100">Batal</button>
              <button onClick={summarize} disabled={!!busy} data-testid="summarize-confirm" className="btn-primary flex items-center gap-2 py-2">{busy === "summary" && <Loader2 size={14} className="animate-spin" />} Ya, buat ringkasan</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Read-only archive viewer (raw messages that were summarized away).
export function LoadMore({ onClick, loading, testid = "load-more" }) {
  return (
    <div className="flex justify-center py-3">
      <button onClick={onClick} disabled={loading} data-testid={testid} className="flex items-center gap-2 rounded-full border border-[#E7ECF3] bg-white px-4 py-1.5 text-xs font-semibold text-[#2F6BFF] hover:bg-[#EEF3FF] disabled:opacity-60">{loading && <Loader2 size={12} className="animate-spin" />} Muat lebih banyak</button>
    </div>
  );
}
